import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AppState } from "../../reducers";
import { AppBar, Button, Dialog, DialogActions, DialogContent, IconButton, Radio, Stack, Step, stepConnectorClasses, StepIconProps, StepLabel, stepLabelClasses, Stepper, Toolbar, Typography } from "@mui/material";
import { CheckCircle, Close, KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { resetAddCircuit, toggleCreate } from "./redux/actions";
import { CircuitCreateStepperPackageType } from "./circuitCreateStepperPackageType";
import { CircuitCreateStepperPackageStockType } from "./circuitCreateStepperPackageStockType";
import { CircuitCreateStepperPackageInformation } from "./circuitCreateStepperPackageInformation";
import { CircuitEditInfoInputs } from "./circuitEditInfo";
import { useCircuitCreate } from "./network/circuitCreate";
import { setLoading } from "../utils/loading/redux/actions";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useShowError } from "../../functions/useShowError";
import { setCurrentLocale } from "../locale/redux/actions";
import { updateCircuitInfoErrors } from "./redux/reducer";
import { Circuit } from "./objects/circuit";
import { User } from "../user/objects/user";
import { getCreateStockVariables } from "./utils/getCreateStockVariables";
import { getPackageFeatureKeys } from "./utils/circuitPackageFeatures";
import { CircuitCreateStepperPackageStockByRoom } from "./circuitCreateStepperPackageStockByRoom";
import { CircuitCreateStepperPackagePriceType } from "./circuitCreateStepperPackagePriceType";
import { CircuitCreateStepperPackageIsDynamicPackage } from "./circuitCreateStepperPackageIsDynamicPackage";
import { CircuitCreateStepperPackageWithoutProduct } from "./circuitCreateStepperPackageWithoutProduct";
import { CircuitCreateStepperPackageIsSkeleton } from "./circuitCreateStepperPackageIsSkeleton";

const stepperStyles = {
    [`& .${stepLabelClasses.alternativeLabel}`]: { marginTop: '0 !important' },
    [`& .${stepLabelClasses.iconContainer}`]: { padding: '0 !important' },
    [`& .${stepLabelClasses.root}`]: { flexDirection: 'column-reverse !important', gap: '10px' },
    [`& .${stepConnectorClasses.root}`]: { marginTop: '30px !important' },
    [`& .${stepConnectorClasses.line}`]: {
        border: "none !important", // Supprime la bordure par défaut
        height: "10px",
        backgroundImage: "radial-gradient(circle, #D9D9D9 30%, transparent 45%)",
        backgroundSize: "13px 16px",
        backgroundRepeat: "repeat-x",
        backgroundPosition: 'center'
    },
    maxWidth: 'md',
    width: '100%'
};
const radioButtonStyle = {
    padding: 0,
    color: '#D9D9D9',
    '&.Mui-checked': {
        color: '#06B217',
    },
    '& .MuiSvgIcon-root': {
        fontSize: 32,
    }
};

const stepContainer = {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'center'
};

type StepKey =
    | 'type'
    | 'typeOfProduct'
    | 'IsDynamicPackage'
    | 'withoutProduct'
    | 'is_skeleton'
    | 'priceType'
    | 'stockType'
    | 'stockByRoom'
    | 'info';

type Step = {
    key: StepKey;
    component: (stepIndex: number, validStep?: () => void) => React.ReactNode;
};



const StepIcon: FC<StepIconProps> = (props) => {
    const { active, completed, className } = props;
    return completed ? <CheckCircle sx={{ color: '#06B217', fontSize: 32 }} className={className} /> :
        active ? <Radio checked sx={radioButtonStyle} className={className} /> :
            <Radio sx={radioButtonStyle} />;
};
export enum CircuitCreateStepperDataPackageType {
    WITH_PRODUCT = 'WITH_PRODUCT',
    WITHOUT_PRODUCT = 'WITHOUT_PRODUCT',
    DYNAMIC = 'DYNAMIC',
    SKELETON_PACKAGE = 'SKELETON_PACKAGE',
    UNKNOWN = 'UNKNOWN',
}

export enum CircuitTypeOfProduct {
    ONLINE = "ONLINE",
    OFFLINE = "OFFLINE",
    MIX = "MIX"
}

export enum CircuitCreateStepperStockType {
    PACKAGE_WITHOUT_STOCK = 'PACKAGE_WITHOUT_STOCK',
    STOCK_ON_THE_PACKAGE = 'STOCK_ON_THE_PACKAGE',
    STOCK_ON_THE_PRODUCT = 'STOCK_ON_THE_PRODUCT',
    STOCK_ON_THE_PRODUCT_AND_PACKAGE = 'STOCK_ON_THE_PRODUCT_AND_PACKAGE',
}

export interface CircuitCreateStepperData {
    package_type?: CircuitCreateStepperDataPackageType;
    stock_by_rooms?: boolean;
    stock_type: CircuitCreateStepperStockType;
    without_products: boolean;
    forfait_price: boolean;
    price_mix: boolean;
    is_skeleton?: boolean;
    is_dynamic_package?: boolean;
}

const EMPTY_CIRCUIT_CREATE_DATA: CircuitCreateStepperData = {
    without_products: true,
    stock_type: CircuitCreateStepperStockType.STOCK_ON_THE_PACKAGE,
    forfait_price: false,
    price_mix: false,
    stock_by_rooms: true,
    is_skeleton: false,
    is_dynamic_package: true,
};


export const CircuitCreateStepper: FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const showError = useShowError();
    const { enqueueSnackbar } = useSnackbar();
    const locale = useSelector((state: AppState) => state.locale.current_locale);
    const create = useSelector((state: AppState) => state.circuit.create);
    // const contract = useSelector((state: AppState) => state.circuit.contract_contract);
    const categories = useSelector((state: AppState) => state.circuit.categories);
    const circuit_name_locales = useSelector((state: AppState) => state.circuit.circuit_name_locales);
    const destination = useSelector((state: AppState) => state.circuit.destination);
    const duration_day = useSelector((state: AppState) => state.circuit.duration_days);
    const duration_night = useSelector((state: AppState) => state.circuit.duration_nights);
    const type = useSelector((state: AppState) => state.circuit.type);
    const reference = useSelector((state: AppState) => state.circuit.reference);
    const external_reference = useSelector((state: AppState) => state.circuit.external_reference);
    const is_protected_description = useSelector((state: AppState) => state.circuit.is_protected_description);
    const display_for_package = useSelector((state: AppState) => state.circuit.display_for_package);
    const display_for_trip_plan = useSelector((state: AppState) => state.circuit.display_for_trip_plan);
    const circuit_info_errors = useSelector((state: AppState) => state.circuitSlice.circuit_info_errors);
    const [steps, setSteps] = useState<Step[]>([]);
    const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
    const [data, setData] = useState<CircuitCreateStepperData>(EMPTY_CIRCUIT_CREATE_DATA);
    const [userTO, setUserTO] = useState<User | null>(null);
    const createCircuit = useCircuitCreate({
        onTrigger: () => {
            dispatch(setLoading(true));
        },
        onSuccess: (circuit) => {
            enqueueSnackbar(t('shared.circuit-created'), { variant: 'success' });
            dispatch(toggleCreate());
            navigate(`/circuit-edit/${circuit.id}`);
        },
        onError: (error) => {
            showError(error);
        },
        onFinally: () => {
            dispatch(setLoading(false));
        }
    });

    const onClose = () => {
        dispatch(toggleCreate());
    };

    const nextStep = useCallback(() => {
        setActiveStepIndex((i) => Math.min(i + 1, steps.length - 1));
    }, [steps]);
    const previousStep = () => {
        setActiveStepIndex((i) => Math.max(i - 1, 0));
    };
    const goToStep = (stepIndex: number) => {
        // Permet uniquement de revenir en arrière (sur les étapes déjà passées)
        if (stepIndex < activeStepIndex) {
            setActiveStepIndex(stepIndex);
        }
    };
    const canNext = activeStepIndex < steps.length - 1;
    const canPrevious = activeStepIndex > 0;
    const canValidate = activeStepIndex === steps.length - 1;

    const getPriceOptionsCount = (package_type?: CircuitCreateStepperDataPackageType): number => {
        if (!package_type) return 0;

        const featureKeys = getPackageFeatureKeys(package_type);
        let count = 0;

        if (featureKeys.includes('manual-price')) count++;
        if (featureKeys.includes('auto-manual-mix')) count++;
        if (featureKeys.includes('auto-price')) count++;

        return count;
    };

    const getSteps = (data: CircuitCreateStepperData): Step[] => {
        const steps: Step[] = [];

        steps.push({
            key: 'type',
            component: (stepIndex, validStep) => <CircuitCreateStepperPackageType stepIndex={stepIndex} next={validStep ?? (() => { return; })} setData={setData} />,
        });

        // Si le type est UNKNOWN, on affiche toutes les étapes de configuration
        if (data.package_type === CircuitCreateStepperDataPackageType.UNKNOWN) {
            steps.push({
                key: 'withoutProduct',
                component: (stepIndex) => <CircuitCreateStepperPackageWithoutProduct stepIndex={stepIndex} setData={setData} data={data} />,
            });
        }

        if (data.package_type !== CircuitCreateStepperDataPackageType.DYNAMIC) {
            steps.push({
                key: 'stockType',
                component: (stepIndex) => <CircuitCreateStepperPackageStockType stepIndex={stepIndex} setData={setData} data={data} />,
            });
        }



        // Ajouter l'étape is_skeleton après allowProductTab si UNKNOWN ou certaines conditions
        if (data.package_type === CircuitCreateStepperDataPackageType.UNKNOWN && !data.without_products && (data.stock_type === CircuitCreateStepperStockType.STOCK_ON_THE_PACKAGE || data.stock_type === CircuitCreateStepperStockType.STOCK_ON_THE_PRODUCT_AND_PACKAGE)) {
            steps.push({
                key: 'is_skeleton',
                component: (stepIndex) => <CircuitCreateStepperPackageIsSkeleton stepIndex={stepIndex} setData={setData} data={data} />,
            });
        }

        const includeStockByRoom = (data.stock_type === CircuitCreateStepperStockType.STOCK_ON_THE_PACKAGE || data.stock_type === CircuitCreateStepperStockType.STOCK_ON_THE_PRODUCT_AND_PACKAGE);
        if (includeStockByRoom) {
            steps.push({
                key: 'stockByRoom',
                component: (stepIndex) => <CircuitCreateStepperPackageStockByRoom stepIndex={stepIndex} setData={setData} data={data} />,
            });
        }

        if (!data.without_products) {
            // Ajouter l'étape allowProductTab pour distinguer un package avec produit avec un package dynamique
            if ((data.package_type === CircuitCreateStepperDataPackageType.UNKNOWN && !data.is_skeleton && data.stock_type === CircuitCreateStepperStockType.PACKAGE_WITHOUT_STOCK)) {
                steps.push({
                    key: 'IsDynamicPackage',
                    component: (stepIndex) => <CircuitCreateStepperPackageIsDynamicPackage stepIndex={stepIndex} setData={setData} data={data} />,
                });
            }

            // N'ajoute l'étape priceType que si le package a plus d'une option de prix
            const priceOptionsCount = getPriceOptionsCount(data.package_type);
            if (priceOptionsCount > 1) {
                steps.push({
                    key: 'priceType',
                    component: (stepIndex) => <CircuitCreateStepperPackagePriceType stepIndex={stepIndex} setData={setData} data={data} />,
                });
            }
        }

        steps.push({
            key: 'info',
            component: (stepIndex) => <CircuitCreateStepperPackageInformation userTO={{ userTO, setUserTO }} />,
        });

        return steps;
    };


    useEffect(() => {
        const updatedSteps = getSteps(data);
        setSteps(updatedSteps);
    }, [data, userTO]);

    const renderStepContent = () => {
        return steps[activeStepIndex]?.component(activeStepIndex, nextStep) || null;
    };




    const onCreate = (inputs: CircuitEditInfoInputs) => {
        if (inputs.destination) {
            createCircuit({
                ...inputs,
                destination: inputs.destination,
                localization: inputs.localization,
                name: inputs.name ?? '',
                type: inputs.type ?? 'CIRCUIT',
                categories: inputs.categories ?? [],
                // contract: contract?.id ?? undefined,
                user_to: inputs.userTO ?? undefined
            });
        }
    };

    const validate = async () => {
        let form_error = circuit_info_errors;
        let is_error = false;
        if (!destination) {
            form_error = { ...form_error, destination: t('destination-params.destination-required') };
            enqueueSnackbar(t('destination-params.destination-required'), { variant: 'error' });
            is_error = true;
        }
        if (!reference) {
            form_error = { ...form_error, reference: t('shared.reference-required') };
            enqueueSnackbar(t('shared.reference-required'), { variant: 'error' });
            is_error = true;
        }
        let no_name = true;
        if (circuit_name_locales[Number(locale || 0)]) {
            no_name = false;
        }
        let too_much_index: null | number = null;
        Object.keys(circuit_name_locales).map((key: string | number) => {
            if ((circuit_name_locales[key]?.length || 0) > 100) {
                too_much_index = Number(key);
            }
        });
        if (no_name) {
            form_error = { ...form_error, name: t('shared.name-required') };
            enqueueSnackbar(t('shared.name-required'), { variant: 'error' });
            is_error = true;
        }
        if (too_much_index) {
            form_error = { ...form_error, name: t('shared.name-should-inf-to-100') };
            dispatch(setCurrentLocale(too_much_index));
            enqueueSnackbar(t('shared.name-should-inf-to-100'), { variant: 'error' });
            is_error = true;
        }
        if (is_error) {
            dispatch(updateCircuitInfoErrors(form_error));
            return;
        }
        if (destination) {
            // Déduire le package_type si UNKNOWN en fonction des choix
            let deduced_package_type = data.package_type;
            if (data.package_type === CircuitCreateStepperDataPackageType.UNKNOWN) {
                // Logique de déduction basée sur les choix
                // Pour l'instant, on met un type par défaut
                if (data.without_products) {
                    deduced_package_type = CircuitCreateStepperDataPackageType.WITHOUT_PRODUCT;
                } else if (data.is_skeleton) {
                    deduced_package_type = CircuitCreateStepperDataPackageType.SKELETON_PACKAGE;
                } else if (data.is_dynamic_package) {
                    deduced_package_type = CircuitCreateStepperDataPackageType.DYNAMIC;
                } else {
                    deduced_package_type = CircuitCreateStepperDataPackageType.WITH_PRODUCT;
                }
            }

            const create_form_data = {
                name: locale !== null ? circuit_name_locales[locale] : undefined,
                localization: Object.keys(circuit_name_locales).map((key) => {
                    const locale = parseInt(key);
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { id, ...localizationRest } = {
                        id: 0,
                        locale,
                        name: null,
                        sale_pitch: null,
                        board_type: null,
                        condition: null,
                        description: null,
                    };
                    return {
                        ...localizationRest,
                        locale,
                        name: circuit_name_locales[locale] ?? null
                    };
                }),
                destination: destination?.id,
                duration_day,
                duration_night,
                type: (type as Circuit['type'] | null) ?? undefined,
                reference: reference ?? '',
                external_reference: external_reference || null,
                display_for_package,
                display_for_trip_plan,
                category: null,
                categories: categories?.map((item) => item.id) ?? undefined,
                userTO: userTO?.id ?? null,
                is_protected_description: !!is_protected_description,
                without_products: data.without_products,
                package_type: deduced_package_type,
                stock_by_rooms: (data.stock_type === CircuitCreateStepperStockType.STOCK_ON_THE_PACKAGE || data.stock_type === CircuitCreateStepperStockType.STOCK_ON_THE_PRODUCT_AND_PACKAGE) ? data.stock_by_rooms : true,
                forfait_price: data.without_products ? false : data.forfait_price,
                price_mix: data.without_products ? false : data.price_mix,
                ...getCreateStockVariables(data.stock_type),
            };
            onCreate(create_form_data);
        }
    };

    useEffect(() => {
        // setActivatedCreateStep(steps[0]);
        if (create) {
            dispatch(resetAddCircuit());
        }
        setActiveStepIndex(0);
    }, [create]);


    return (<Dialog fullScreen open={create} onClose={onClose}>
        <AppBar className={'ft-user-appBar'}>
            <Toolbar>
                <IconButton edge={'start'} color={'inherit'} onClick={onClose}><Close /></IconButton>
                <Typography variant={'h6'}>{t('shared.add-circuit-package')}</Typography>
            </Toolbar>
        </AppBar>
        <DialogContent>
            <Stack direction="column" gap={12}>
                <Stack direction={'row'} justifyContent={'center'} alignItems={'center'}>
                    <Stepper sx={stepperStyles} activeStep={activeStepIndex}>
                        {steps.map((step, index) => {
                            const isCompleted = index < activeStepIndex;
                            const isClickable = isCompleted;
                            return (
                                <Step key={`step-${index}-${activeStepIndex}`} disabled={!isClickable}>
                                    <StepLabel
                                        sx={{
                                            pointerEvents: isClickable ? 'auto' : 'none',
                                            cursor: isClickable ? 'pointer' : 'default'
                                        }}
                                        onClick={() => isClickable && goToStep(index)}
                                        StepIconComponent={StepIcon}
                                    >
                                        {t('shared.step-number', { count: index + 1 })}
                                    </StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                </Stack>
                <Stack sx={stepContainer}>
                    {renderStepContent()}
                </Stack>
                {activeStepIndex !== 0 && <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} gap={2}>
                    <Button onClick={previousStep} disabled={!canPrevious} startIcon={<KeyboardArrowLeft />} >{t('shared.previous')}</Button>
                    |
                    {!canValidate
                        ? <Button onClick={nextStep} disabled={!canNext} variant="contained" endIcon={<KeyboardArrowRight />}>{t('shared.next')}</Button>
                        : <Button onClick={validate} disabled={!canValidate} variant="contained">{t('shared.save')}</Button>}
                </Stack>}
            </Stack>
        </DialogContent>
        <DialogActions sx={{ padding: '8px 24px' }}>
        </DialogActions>
    </Dialog>);
};